
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Plus, Trash2, Edit, Save, X } from 'lucide-react';
import AddItemModal from '../../components/AddItemModal';
import SortableMasterTemplateSection from '../../components/SortableMasterTemplateSection';

const AdminMasterTemplatePage = ({ title, template, onTemplateUpdate, itemType, templateKeyPrefix }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentType, setCurrentType] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [editedItem, setEditedItem] = useState(null);
    const [localTemplate, setLocalTemplate] = useState(template);
    const hasLoadedRef = useRef(false);
    const loadedTemplateTypeRef = useRef(null);

    // Ensure modal is closed on unmount
    useEffect(() => {
        return () => {
            setIsModalOpen(false);
            setCurrentType(null);
        };
    }, []);

    // Determine template type from templateKeyPrefix - memoize to prevent unnecessary re-renders
    const templateType = useMemo(() => {
        return templateKeyPrefix === 'ff' ? 'factfind' : 
               templateKeyPrefix === 'link' ? 'links' : 
               templateKeyPrefix || 'factfind';
    }, [templateKeyPrefix]);

    const loadFreshTemplate = useCallback(async () => {
        try {
            const response = await fetch(`/api/admin/templates/${templateType}`);
            if (response.ok) {
                const freshTemplate = await response.json();
                // Transform to match expected format
                const transformed = {
                    sales: (freshTemplate.sales || []).map(field => ({
                        id: field.key || field.field_key,
                        key: field.key || field.field_key,
                        label: field.label || field.field_label,
                        type: field.type || field.field_type,
                        options: field.options || field.field_options,
                        is_required: field.is_required,
                        order: field.order || field.display_order || 0
                    })),
                    service: (freshTemplate.service || []).map(field => ({
                        id: field.key || field.field_key,
                        key: field.key || field.field_key,
                        label: field.label || field.field_label,
                        type: field.type || field.field_type,
                        options: field.options || field.field_options,
                        is_required: field.is_required,
                        order: field.order || field.display_order || 0
                    }))
                };
                setLocalTemplate(transformed);
                // Don't call onTemplateUpdate here to prevent infinite loop
                // The parent will get updated when user makes changes
            } else {
                // Fallback to prop
                setLocalTemplate(template);
            }
        } catch (error) {
            console.error('Error loading fresh template:', error);
            // Fallback to prop - use current template prop value
            setLocalTemplate(template);
        }
    }, [templateType]); // Don't include template to prevent recreation on every prop change

    // Load fresh templates from API on mount only (not when template prop changes)
    useEffect(() => {
        // Only load if:
        // 1. It's not a Task type
        // 2. We have a templateType
        // 3. We haven't loaded this templateType yet (or it changed)
        if (itemType !== 'Task' && templateType) {
            if (!hasLoadedRef.current || loadedTemplateTypeRef.current !== templateType) {
                hasLoadedRef.current = true;
                loadedTemplateTypeRef.current = templateType;
                loadFreshTemplate();
            }
        } else if (itemType === 'Task') {
            setLocalTemplate(template);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemType, templateType, loadFreshTemplate]); // Removed 'template' to prevent infinite loop

    const handleDelete = async (type, itemId) => {
        if (itemType === 'Task') {
            // For checklist items, use the existing flow
            onTemplateUpdate({ ...localTemplate, [type]: localTemplate[type].filter(t => t.id !== itemId) });
        } else {
            // For factfind/other template fields, delete from database
            try {
                const response = await fetch(`/api/admin/templates/${itemId}`, {
                    method: 'DELETE'
                });

                if (response.ok) {
                    // Reload fresh template after deletion
                    await loadFreshTemplate();
                } else {
                    const error = await response.json();
                    alert(`Failed to delete field: ${error.error || 'Unknown error'}`);
                }
            } catch (error) {
                console.error('Error deleting template field:', error);
                alert('Failed to delete field. Please try again.');
            }
        }
    };

    const handleUpdate = async (type, updatedItem) => {
        if (itemType === 'Task') {
            // For checklist items, use the existing flow
            onTemplateUpdate({
                ...template,
                [type]: template[type].map(item => item.id === updatedItem.id ? updatedItem : item)
            });
        } else {
            // For factfind/other template fields, update in database
            try {
                const response = await fetch(`/api/admin/templates/${updatedItem.key || updatedItem.id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        field_label: updatedItem.label,
                        is_required: updatedItem.is_required || false,
                        display_order: updatedItem.order || 0
                    })
                });

                if (response.ok) {
                    // Reload fresh template after update
                    await loadFreshTemplate();
                } else {
                    const error = await response.json();
                    alert(`Failed to update field: ${error.error || 'Unknown error'}`);
                }
            } catch (error) {
                console.error('Error updating template field:', error);
                alert('Failed to update field. Please try again.');
            }
        }
    };

    const openAddItemModal = (type) => {
        setCurrentType(type);
        setIsModalOpen(true);
    };

    const handleAddItem = async (formData) => {
        if (itemType === 'Task') {
            // For checklist items, use the existing flow
            const newItem = { 
                id: `${templateKeyPrefix}-${currentType}-${Date.now()}`, 
                task: formData.itemName, 
                category: formData.category || 'New Tasks' 
            };
            onTemplateUpdate({ ...template, [currentType]: [...(template[currentType] || []), newItem] });
            // Modal will be closed by AddItemModal's handleSave after onSave completes
            return Promise.resolve();
        } else {
            // For factfind/other template fields, save directly to database
            try {
                // Determine template_type from templateKeyPrefix or context
                // templateKeyPrefix should be like 'ff' for factfind
                const templateType = templateKeyPrefix === 'ff' ? 'factfind' : 
                                   templateKeyPrefix === 'link' ? 'links' : 
                                   templateKeyPrefix || 'factfind';
                
                // Generate field_key from label if not provided
                const fieldKey = formData.itemKey || formData.itemLabel
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, '_')
                    .replace(/^_+|_+$/g, '');

                // Determine field_type and field_options
                const fieldType = formData.itemFieldType || 'text';
                let fieldOptions = null;
                
                // If field type is 'brand', fetch brands and set as options
                if (fieldType === 'brand') {
                    try {
                        const brandsResponse = await fetch('/api/brands');
                        if (brandsResponse.ok) {
                            const brands = await brandsResponse.json();
                            fieldOptions = brands.map(b => b.name);
                        }
                    } catch (err) {
                        console.error('Error fetching brands for field options:', err);
                    }
                }

                const response = await fetch('/api/admin/templates', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        template_type: templateType,
                        category: currentType, // 'sales' or 'service'
                        field_key: fieldKey,
                        field_label: formData.itemLabel,
                        field_type: fieldType,
                        field_options: fieldOptions ? JSON.stringify(fieldOptions) : null,
                        is_required: false,
                        display_order: (localTemplate[currentType]?.length || 0) + 1
                    })
                });

                if (response.ok) {
                    // Reload fresh template after creation
                    await loadFreshTemplate();
                    // Modal will be closed by AddItemModal's handleSave after onSave completes
                    return Promise.resolve();
                } else {
                    const error = await response.json();
                    alert(`Failed to create field: ${error.error || 'Unknown error'}`);
                    // Don't close modal on error so user can fix and retry
                    return Promise.reject(new Error(error.error || 'Unknown error'));
                }
            } catch (error) {
                console.error('Error creating template field:', error);
                alert('Failed to create field. Please try again.');
                // Don't close modal on error so user can fix and retry
                return Promise.reject(error);
            }
        }
    };

    const startEditing = (item) => {
        setEditingId(item.id);
        setEditedItem({ ...item });
    };

    const cancelEditing = () => {
        setEditingId(null);
        setEditedItem(null);
    };

    const saveEditing = (type) => {
        handleUpdate(type, editedItem);
        cancelEditing();
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditedItem(prev => ({ ...prev, [name]: value }));
    };

    const handleReorder = async (type, orderedIds) => {
        // Reorder the items based on the new order
        const currentItems = localTemplate[type] || [];
        const reorderedItems = orderedIds.map(id =>
            currentItems.find(item => item.id === id)
        ).filter(Boolean);

        // Update the template with the new order
        const updatedTemplate = {
            ...localTemplate,
            [type]: reorderedItems
        };
        setLocalTemplate(updatedTemplate);

        // If not a Task (i.e., factfind fields), update display_order in database
        if (itemType !== 'Task') {
            try {
                // Update display_order for each field
                const updatePromises = reorderedItems.map((item, index) => {
                    const fieldKey = item.key || item.id;
                    return fetch(`/api/admin/templates/${fieldKey}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            field_label: item.label,
                            is_required: item.is_required || false,
                            display_order: index + 1
                        })
                    });
                });
                await Promise.all(updatePromises);
                // Reload to ensure consistency - don't call onTemplateUpdate as it will reload anyway
                await loadFreshTemplate();
            } catch (error) {
                console.error('Error updating field order:', error);
            }
        } else {
            // For Tasks, update parent immediately
            onTemplateUpdate(updatedTemplate);
        }
    };

    const modalFields = itemType === "Task"
        ? [{ name: "Task Name", id: "itemName" }, { name: "Category", id: "category" }]
        : [
            { name: "Display Label", id: "itemLabel" },
            { name: "System Key", id: "itemKey" },
            { 
                name: "Field Type", 
                id: "itemFieldType", 
                type: "select",
                options: ['text', 'textarea', 'email', 'tel', 'url', 'number', 'date', 'select', 'brand']
            }
          ];

    const renderSection = (type) => (
        <SortableMasterTemplateSection
            type={type}
            title={title}
            itemType={itemType}
            items={localTemplate[type] || []}
            editingId={editingId}
            editedItem={editedItem}
            onStartEditing={startEditing}
            onCancelEditing={cancelEditing}
            onSaveEditing={saveEditing}
            onEditChange={handleEditChange}
            onDelete={handleDelete}
            onReorder={handleReorder}
            onOpenAddModal={openAddItemModal}
        />
    );

    return (
        <div style={{ position: 'relative', zIndex: 1 }}>
            <div className="mb-6">
                <h2 className="text-2xl font-bold">Master {title} Template</h2>
                <p className="text-gray-600 mt-1">Configure the {title.toLowerCase()} fields. Drag the grip handle to reorder items within each column.</p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-6">
                {renderSection('sales')}
                {renderSection('service')}
            </div>
            {isModalOpen && (
                <AddItemModal
                    isOpen={isModalOpen}
                    onClose={() => {
                        setIsModalOpen(false);
                        setCurrentType(null);
                    }}
                    onSave={handleAddItem}
                    title={`Add New ${itemType}`}
                    fields={modalFields}
                />
            )}
        </div>
    );
};

export default AdminMasterTemplatePage;
